import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  updateFacility, 
  fetchFacilities, 
  fetchUserFacilities, 
  fetchFacilitiesByIds,
  fetchFacilityById,
  createFacility
} from '../thunks/facilitiesThunks';

/**
 * Sanitize facility data to ensure Redux serialization compatibility
 * @param {any} data - Facility data to sanitize
 * @returns {any} Sanitized facility data
 */
const sanitizeFacilityData = (data) => {
  if (!data || typeof data !== 'object') return data;
  
  const sanitized = { ...data };
  
  Object.keys(sanitized).forEach(key => {
    const value = sanitized[key];
    
    // Check if value is a Firestore Timestamp
    if (value && typeof value === 'object' && value._seconds !== undefined && value._nanoseconds !== undefined) {
      // Convert Timestamp to ISO string for Redux serialization
      const date = new Date(value._seconds * 1000 + value._nanoseconds / 1000000);
      sanitized[key] = date.toISOString();
    } 
    // Handle Date objects - convert to ISO string for Redux serialization
    else if (value instanceof Date) {
      sanitized[key] = value.toISOString();
    }
    // Handle nested objects and arrays
    else if (value && typeof value === 'object') {
      if (Array.isArray(value)) {
        sanitized[key] = value.map(item => 
          typeof item === 'object' ? sanitizeFacilityData(item) : item
        );
      } else {
        sanitized[key] = sanitizeFacilityData(value);
      }
    }
  });
  
  // Special handling for encounterTypes - ensure they are deduplicated
  if (sanitized.encounterTypes && Array.isArray(sanitized.encounterTypes)) {
    sanitized.encounterTypes = [...new Set(sanitized.encounterTypes)];
  }
  
  return sanitized;
};

const initialState = {
  facilities: [],
  userFacilities: [],
  selectedFacility: null,
  selectedFacilities: [],
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0
  },
  lastFetched: null
};

const facilitiesSlice = createSlice({
  name: 'facilities',
  initialState,
  reducers: {
    setSelectedFacilities: (state, action) => {
      state.selectedFacilities = action.payload;
      state.loading = false;
      state.error = null;
    },
    setFacilities: (state, action) => {
      // Sanitize facility data to ensure Redux serialization compatibility
      const sanitizedFacilities = Array.isArray(action.payload) 
        ? action.payload.map(facility => sanitizeFacilityData(facility))
        : sanitizeFacilityData(action.payload);
      
      state.facilities = sanitizedFacilities;
      state.loading = false;
      state.error = null;
    },
    setUserFacilities: (state, action) => {
      // Sanitize facility data to ensure Redux serialization compatibility
      const sanitizedFacilities = Array.isArray(action.payload) 
        ? action.payload.map(facility => sanitizeFacilityData(facility))
        : sanitizeFacilityData(action.payload);
      
      state.userFacilities = sanitizedFacilities;
      state.loading = false;
      state.error = null;
    },
    setSelectedFacility: (state, action) => {
      // Sanitize facility data to ensure Redux serialization compatibility
      state.selectedFacility = action.payload ? sanitizeFacilityData(action.payload) : null;
      state.loading = false;
      state.error = null;
    },
    setLoading: (state) => {
      state.loading = true;
      state.error = null;
    },
    setError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearFacilities: (state) => {
      state.facilities = [];
      state.userFacilities = [];
      state.selectedFacility = null;
      state.selectedFacilities = [];
      state.loading = false;
      state.error = null;
      state.pagination = {
        currentPage: 1,
        totalPages: 1,
        totalCount: 0
      };
      state.lastFetched = null;
    },
    clearFacilityCache: (state) => {
      // Clear all facility data and force fresh fetch
      state.facilities = [];
      state.userFacilities = [];
      state.selectedFacility = null;
      state.selectedFacilities = [];
      state.loading = false;
      state.error = null;
      state.pagination = {
        currentPage: 1,
        totalPages: 1,
        totalCount: 0
      };
      state.lastFetched = null;
    },
    setLastFetched: (state, action) => {
      state.lastFetched = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Handle fetchFacilities
    builder
      .addCase(fetchFacilities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFacilities.fulfilled, (state, action) => {
        state.loading = false;
        // Sanitize facility data to ensure Redux serialization compatibility
        const sanitizedFacilities = Array.isArray(action.payload) 
          ? action.payload.map(facility => sanitizeFacilityData(facility))
          : sanitizeFacilityData(action.payload);
        
        state.facilities = sanitizedFacilities;
        // For admin users, also populate userFacilities so components can access them
        state.userFacilities = sanitizedFacilities;
        state.error = null;
        state.lastFetched = Date.now();
      })
      .addCase(fetchFacilities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

    // Handle fetchUserFacilities
      .addCase(fetchUserFacilities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserFacilities.fulfilled, (state, action) => {
        state.loading = false;
        // Sanitize facility data to ensure Redux serialization compatibility
        const sanitizedFacilities = Array.isArray(action.payload.facilities) 
          ? action.payload.facilities.map(facility => sanitizeFacilityData(facility))
          : sanitizeFacilityData(action.payload.facilities);
        
        state.userFacilities = sanitizedFacilities;
        state.pagination = {
          currentPage: action.payload.currentPage,
          totalPages: action.payload.totalPages,
          totalCount: action.payload.totalCount
        };
        state.error = null;
        state.lastFetched = Date.now();
      })
      .addCase(fetchUserFacilities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

    // Handle fetchFacilitiesByIds
      .addCase(fetchFacilitiesByIds.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFacilitiesByIds.fulfilled, (state, action) => {
        state.loading = false;
        // Sanitize facility data to ensure Redux serialization compatibility
        const sanitizedFacilities = Array.isArray(action.payload) 
          ? action.payload.map(facility => sanitizeFacilityData(facility))
          : sanitizeFacilityData(action.payload);
        
        state.selectedFacilities = sanitizedFacilities;
        state.error = null;
      })
      .addCase(fetchFacilitiesByIds.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

    // Handle fetchFacilityById
      .addCase(fetchFacilityById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFacilityById.fulfilled, (state, action) => {
        state.loading = false;
        // Sanitize facility data to ensure Redux serialization compatibility
        state.selectedFacility = action.payload ? sanitizeFacilityData(action.payload) : null;
        state.error = null;
      })
      .addCase(fetchFacilityById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

    // Handle updateFacility
      .addCase(updateFacility.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateFacility.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Sanitize facility data to ensure Redux serialization compatibility
        const updatedFacility = sanitizeFacilityData(action.payload);
        
        // Update selectedFacility
        state.selectedFacility = updatedFacility;
        
        // Update in facilities array
        state.facilities = state.facilities.map(facility => 
          facility.id === updatedFacility.id ? updatedFacility : facility
        );
        
        // Update in userFacilities array
        state.userFacilities = state.userFacilities.map(facility => 
          facility.id === updatedFacility.id ? updatedFacility : facility
        );
      })
      .addCase(updateFacility.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update facility';
      })

    // Handle createFacility
      .addCase(createFacility.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createFacility.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Sanitize facility data to ensure Redux serialization compatibility
        const newFacility = sanitizeFacilityData(action.payload);
        
        // Add to facilities array
        state.facilities.push(newFacility);
        
        // Add to userFacilities array
        state.userFacilities.push(newFacility);
        
        // Set as selected facility
        state.selectedFacility = newFacility;
      })
      .addCase(createFacility.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to create facility';
      });
  },
});

export const {
  setFacilities,
  setUserFacilities,
  setSelectedFacility,
  setSelectedFacilities,
  setLoading,
  setError,
  clearError,
  clearFacilities,
  clearFacilityCache,
  setLastFetched,
} = facilitiesSlice.actions;

// Selectors
export const selectFacilities = (state) => state.facilities.facilities;
export const selectUserFacilities = (state) => state.facilities.userFacilities;
export const selectSelectedFacility = (state) => state.facilities.selectedFacility;
export const selectSelectedFacilities = (state) => state.facilities.selectedFacilities;
export const selectLoading = (state) => state.facilities.loading;
export const selectError = (state) => state.facilities.error;
export const selectPagination = (state) => state.facilities.pagination;
export const selectLastFetched = (state) => state.facilities.lastFetched;

// Utility selectors (replacing cache service functions)
export const selectFacilityById = (state, facilityId) => {
  if (!facilityId) return null;
  return state.facilities.userFacilities.find(f => f.id === facilityId) || null;
};

export const selectFacilitiesByIds = (state, facilityIds) => {
  if (!facilityIds || !Array.isArray(facilityIds)) return [];
  return facilityIds
    .map(id => selectFacilityById(state, id))
    .filter(facility => facility !== null);
};

export const selectDefaultFacility = (state) => {
  const user = state.auth.user;
  if (!user || !user.defaultFacility) return null;
  return selectFacilityById(state, user.defaultFacility);
};

export const selectPrimaryFacility = (state) => {
  const defaultFacility = selectDefaultFacility(state);
  if (defaultFacility) return defaultFacility;
  
  const userFacilities = state.facilities.userFacilities;
  return userFacilities.length > 0 ? userFacilities[0] : null;
};

export const selectHasFacilityAccess = (state, facilityId) => {
  if (!facilityId) return false;
  
  const user = state.auth.user;
  if (!user) return false;
  
  // Admin has access to all facilities
  if (user.roles && user.roles.includes('admin')) return true;
  
  // Check if facility is in user's facilities
  const userFacilities = state.facilities.userFacilities;
  return userFacilities.some(f => f.id === facilityId);
};

export default facilitiesSlice.reducer;
